/**
 * Shared formatting utilities for dates, prices, and measurements.
 * All functions are null/undefined-safe and return "—" for missing data.
 */

/** Format a date ISO string as "12 Jan 2024" */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

/** Format a date ISO string as "12 Jan" (no year — for calendar-dense tables) */
export function fmtDateShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    month: "short",
    day: "numeric",
  }).format(d);
}

/** Format a duration between two ISO strings as "7 days" */
export function fmtDays(start: string | null | undefined, end: string | null | undefined): string {
  if (!start || !end) return "—";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (Number.isNaN(ms)) return "—";
  const days = Math.round(ms / 86_400_000);
  return `${days} day${days !== 1 ? "s" : ""}`;
}

/** Format a USD price as "$1,500" */
export function fmtPrice(amount: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Format a nautical miles value as "142 nm" */
export function fmtMiles(n: number | null | undefined): string {
  if (n == null) return "—";
  return `${n.toLocaleString("en-GB")} nm`;
}

/** Format an ISO timestamp as a human-relative string ("2 hours ago", "just now", etc.) */
export function fmtRelativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  // graphql-js serializes Date objects for String! fields as numeric ms strings
  // via String(date.valueOf()). new Date(numericString) gives Invalid Date in
  // browsers — convert to number first.
  const numTs = Number(iso);
  const d = Number.isFinite(numTs) ? new Date(numTs) : new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return fmtDate(iso);
}
